import { useState } from "react";
import { Link } from "react-router";
import { CodeInput, PasswordInput } from "./AuthShell";
import { formatRelative } from "../lib/format";
import { acceptAmount, chargedMoney, money } from "../lib/money";
import { useParkedPayments, useSettlePayment } from "../lib/queries";
import type { ParkedPayment } from "../lib/types";
import { Modal } from "./Modal";
import { Alert, Button, Card, CardHeader, EmptyState, Field, Input, LoadingBlock, Table, Td, Th } from "./ui";

/**
 * Payments where money moved and credit could not follow.
 *
 * Paid short -- the read-back said paid for less than due -- or captured in a
 * currency the ledger does not hold. Both park the row `failed` with the
 * capture id, email the customer to write in, and then wait for a person.
 * Nowhere else shows them: the balance is just lower than the customer paid
 * for, and the payment list reads failed. This is the list, and closing a
 * row is the only way off it.
 *
 * Two ways to close. Credit: the platform keeps the money and posts what it
 * bought, under the same step-up and per-operator ceiling as any adjustment.
 * A note alone: the money went back outside the platform, and the row says
 * so. Either way the row records who and why.
 */
export function SettlementQueueCard({ canWrite }: { canWrite: boolean }) {
  const { data, isPending, error } = useParkedPayments();
  const [settling, setSettling] = useState<ParkedPayment | null>(null);
  const rows = data?.data ?? [];

  return (
    <Card>
      <CardHeader
        title="Needs settlement"
        description="Money that arrived and could not be credited: paid short, or in the wrong currency. Each waits for a person."
        action={rows.length > 0 ? <span className="text-sm tabular-nums text-fg-subtle">{rows.length}</span> : undefined}
      />
      {isPending ? (
        <LoadingBlock />
      ) : error ? (
        <Alert>{error.message}</Alert>
      ) : rows.length === 0 ? (
        <EmptyState icon="check" title="Nothing waiting" description="Every payment that could not be credited automatically has been closed." />
      ) : (
        <Table
          head={
            <tr>
              <Th priority="sm">When</Th>
              <Th>Organization</Th>
              <Th>Why</Th>
              <Th numeric>Paid</Th>
              <Th numeric>For</Th>
              {canWrite && <Th />}
            </tr>
          }
        >
          {rows.map((payment) => (
            <tr key={payment.id}>
              <Td className="text-fg-subtle" priority="sm">
                {formatRelative(payment.createdAt)}
              </Td>
              <Td>
                <Link to={`/organizations/${payment.organization.id}/billing`} className="text-fg hover:text-accent">
                  {payment.organization.name}
                </Link>
              </Td>
              <Td className="max-w-md text-xs text-fg-muted">{payment.failureReason ?? "Parked for support"}</Td>
              <Td numeric>
                {payment.charged ? chargedMoney(payment.charged.amount, payment.charged.currency) : "—"}
                <span className="block text-xs text-fg-subtle">{providerName(payment.provider)}</span>
              </Td>
              <Td numeric>{money(payment.amountMicros, payment.currency)}</Td>
              {canWrite && (
                <Td numeric>
                  <Button variant="secondary" size="sm" onClick={() => setSettling(payment)}>
                    Settle
                  </Button>
                </Td>
              )}
            </tr>
          ))}
        </Table>
      )}

      {settling && <SettleModal payment={settling} onClose={() => setSettling(null)} />}
    </Card>
  );
}

function providerName(provider: string): string {
  return ({ polar: "Polar", paypal: "PayPal", borderpay: "BorderPay" } as Record<string, string>)[provider] ?? provider;
}

function SettleModal({ payment, onClose }: { payment: ParkedPayment; onClose: () => void }) {
  const [mode, setMode] = useState<"credit" | "note">("credit");
  const [amount, setAmount] = useState(String(payment.amountMicros / 1_000_000));
  const [note, setNote] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const mutation = useSettlePayment();

  const creditCents = mode === "credit" ? Math.round(Number(amount) * 100) : 0;
  const ready = note.trim().length > 0 && (mode === "note" || (creditCents > 0 && password && code.length >= 6));

  return (
    <Modal
      open
      onClose={onClose}
      title="Settle this payment"
      description={`${payment.organization.name} · ${payment.charged ? chargedMoney(payment.charged.amount, payment.charged.currency) : money(payment.amountMicros, payment.currency)} received for ${money(payment.amountMicros, payment.currency)} of credit.`}
      dismissable={!mutation.isPending}
    >
      <div className="space-y-4">
        {mutation.error && <Alert>{mutation.error.message}</Alert>}
        <p className="text-xs text-fg-muted">{payment.failureReason}</p>

        <div className="flex gap-2" role="radiogroup" aria-label="How it was settled">
          {(
            [
              ["credit", "Keep the money, post credit"],
              ["note", "Refunded outside, close with a note"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={mode === value}
              onClick={() => setMode(value)}
              className={
                mode === value
                  ? "rounded-md border border-accent bg-accent/10 px-3 py-1.5 text-xs text-fg"
                  : "rounded-md border border-line px-3 py-1.5 text-xs text-fg-muted hover:border-line-strong hover:text-fg"
              }
            >
              {label}
            </button>
          ))}
        </div>

        {mode === "credit" && (
          <Field label="Credit to post" htmlFor="settle-amount" hint="In the billing currency. What the money received actually bought -- the full amount if the shortfall is being waived.">
            <Input
              id="settle-amount"
              inputMode="decimal"
              value={amount}
              onChange={(event) => {
                const accepted = acceptAmount(event.target.value);
                if (accepted !== null) setAmount(accepted);
              }}
            />
          </Field>
        )}

        <Field
          label="Note"
          htmlFor="settle-note"
          hint={mode === "credit" ? "Required. Shown to the customer on their ledger, and kept on the payment and in the audit log." : "Required. Kept on the payment and in the audit log."}
        >
          <Input
            id="settle-note"
            value={note}
            placeholder={mode === "credit" ? "Shortfall waived; customer paid the bank's fee" : "Returned by transfer, ref 12345"}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>

        {mode === "credit" && (
          <div className="space-y-3 border-t border-line pt-4">
            <p className="text-xs text-fg-subtle">Posting credit moves money, so it asks for your password and authenticator code again.</p>
            <Field label="Password" htmlFor="settle-password">
              <PasswordInput id="settle-password" size="md" surface="panel" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} />
            </Field>
            <CodeInput id="settle-code" label="Authenticator code" value={code} onChange={setCode} />
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            disabled={!ready}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate(
                {
                  paymentId: payment.id,
                  creditCents,
                  note: note.trim(),
                  ...(mode === "credit" ? { password, code } : {}),
                },
                { onSuccess: onClose },
              )
            }
          >
            {mode === "credit" ? `Post ${money(creditCents * 10_000, payment.currency)} and close` : "Close without credit"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
